import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export type DomainStatus = 'PENDING' | 'VERIFIED' | 'FAILED';

export interface SendingDomain {
  id: string;
  org_id: string;
  domain: string;
  status: DomainStatus;
  is_primary: boolean;
  dkim_verified: boolean;
  spf_verified: boolean;
  dmarc_verified: boolean;
  dmarc_confirmed_at: string | null;
  postmark_domain_id: string | null;
  dkim_host: string | null;
  dkim_text_value: string | null;
  dkim_pending_host: string | null;
  dkim_pending_text_value: string | null;
  return_path_cname: string | null;
  /** Local-part used to build the marketing From: address. */
  from_local_part: string | null;
  /** Display name accompanying the marketing From: address. */
  from_name: string | null;
  last_verified_check_at: string | null;
  last_verified_error: string | null;
  created_at: string;
  updated_at: string;
}

export function useSendingDomains() {
  return useQuery<{ data: SendingDomain[] }>({
    queryKey: ['sending-domains'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: SendingDomain[] }>('/sending-domains');
      return data;
    },
    // Poll briefly while domains are still pending so the UI reflects
    // verification progress without the user having to click Refresh.
    refetchInterval: (q) => {
      const list = q.state.data?.data;
      return list?.some((d) => d.status === 'PENDING') ? 15_000 : false;
    },
  });
}

export function useCreateSendingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (domain: string) => {
      const { data } = await apiClient.post<{ data: SendingDomain }>('/sending-domains', { domain });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sending-domains'] }),
  });
}

export function useVerifySendingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: SendingDomain }>(`/sending-domains/${id}/verify`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sending-domains'] }),
  });
}

export function useConfirmDmarc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: SendingDomain }>(`/sending-domains/${id}/confirm-dmarc`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sending-domains'] }),
  });
}

export function useUpdateFromAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; from_local_part: string | null; from_name: string | null }) => {
      const { data } = await apiClient.patch<{ data: SendingDomain }>(
        `/sending-domains/${args.id}/from-address`,
        { from_local_part: args.from_local_part, from_name: args.from_name }
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sending-domains'] }),
  });
}

export function useSetPrimarySendingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: SendingDomain }>(`/sending-domains/${id}/set-primary`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sending-domains'] }),
  });
}
